import { useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import PageCustomizable from 'hb-react/frontoffice/components/PageCustomizable';
import { HtmlContentWithIframe } from 'hb-react/shared/components/HtmlContent';
import { executeExternalScript } from 'helpers/scripts';

import EventMenu from '../EventMenu';

const Wrapper = ({ children, attributes, className, dataTestId }) => {
  const { key, ...restAttributes } = attributes || {};

  return (
    <div
      className={`grid__item ten-twelfths break-word portable--one-whole ${
        className ?? ''
      }`}
      {...restAttributes}
      key={key}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
};

const Page = ({ page, attributes = {} }) => (
  <Wrapper attributes={attributes}>
    <PageCustomizable {...page} />
  </Wrapper>
);

const Description = ({ description, attributes = {}, className }) => (
  <Wrapper
    attributes={attributes}
    className={className}
    dataTestId="event-content"
  >
    <HtmlContentWithIframe content={description} stylesConfig="web" />
  </Wrapper>
);

const TabbedContent = ({ appPages, slug, defaultTitle, children }) => {
  const tabs = appPages.filter((page) => page.menu.display_in_menu);

  return (
    <>
      <EventMenu appPages={tabs} slug={slug} defaultTitle={defaultTitle} />
      {children}
    </>
  );
};

const SlugBlock = ({ appPages, defaultTitle }) => {
  const { slug } = useParams();
  const page = appPages.find((page) => page.menu.slug === slug);

  if (page) {
    return (
      <TabbedContent
        key={slug}
        appPages={appPages}
        slug={slug}
        defaultTitle={defaultTitle}
      >
        <Page page={page.menu} />
      </TabbedContent>
    );
  }

  return <Navigate to=".." />;
};

const IndexBlock = ({ appPages, defaultTitle, description }) => (
  <TabbedContent appPages={appPages} slug="default" defaultTitle={defaultTitle}>
    <Description
      description={description}
      attributes={{
        key: `event_page_content_default`,
      }}
    />
  </TabbedContent>
);

const EventDetails = ({ event, className, defaultTitle }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { app_pages: appPages = [], description } = event;

  const slug = searchParams.get('tab');

  useEffect(() => {
    if (appPages.find((page) => page.menu.slug === slug)) {
      navigate(`page/${slug}`);
    }
  }, [slug, appPages, navigate]);

  useEffect(() => {
    executeExternalScript(description);
  }, [description]);

  if (appPages.length === 0) {
    return <Description description={description} className={className} />;
  }

  return (
    <section className={className}>
      <Routes>
        <Route
          index
          element={
            <IndexBlock
              appPages={appPages}
              defaultTitle={defaultTitle}
              description={description}
            />
          }
        />
        <Route
          path="page/:slug"
          element={
            <SlugBlock appPages={appPages} defaultTitle={defaultTitle} />
          }
        />
        <Route path="*" element={<Navigate to="." replace />} />
      </Routes>
    </section>
  );
};

export default EventDetails;
